const ISO_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const WEEKDAYS = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];

export function pad2(n) {
  return String(n).padStart(2, "0");
}

export function toDateValue(date) {
  if (!date || Number.isNaN(date.getTime())) return "";
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

export function toDateTimeValue(date) {
  if (!date || Number.isNaN(date.getTime())) return "";
  return `${toDateValue(date)}T${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
}

export function isValidIsoDateString(value) {
  if (!value) return false;
  const raw = String(value).trim();
  if (!ISO_DATE_RE.test(raw)) return false;
  const [, y, m, d] = raw.match(ISO_DATE_RE);
  const year = Number(y);
  const month = Number(m);
  const day = Number(d);
  if (year < 1000 || year > 9999) return false;
  const date = new Date(year, month - 1, day);
  return (
    !Number.isNaN(date.getTime()) &&
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day
  );
}

export function parseDateValue(value) {
  if (!isValidIsoDateString(value)) return null;
  const [, y, m, d] = String(value).trim().match(ISO_DATE_RE);
  const date = new Date(Number(y), Number(m) - 1, Number(d));
  return Number.isNaN(date.getTime()) ? null : date;
}

export function getDefaultPickerMinYear(referenceDate = new Date()) {
  return referenceDate.getFullYear() - 100;
}

export function getDefaultPickerMaxYear(referenceDate = new Date()) {
  return referenceDate.getFullYear() + 10;
}

/** Earliest DOB allowed — employee must be strictly younger than 100 years. */
export function getDobMinDate(referenceDate = new Date()) {
  const d = new Date(referenceDate);
  d.setFullYear(d.getFullYear() - 100);
  d.setDate(d.getDate() + 1);
  return d;
}

export function getDobMaxDate(referenceDate = new Date()) {
  return new Date(referenceDate);
}

export function getAgeYears(dobValue, referenceDate = new Date()) {
  const dob = parseDateValue(dobValue);
  if (!dob) return null;
  const ref = referenceDate instanceof Date ? referenceDate : parseDateValue(referenceDate);
  if (!ref) return null;
  let years = ref.getFullYear() - dob.getFullYear();
  const monthDiff = ref.getMonth() - dob.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && ref.getDate() < dob.getDate())) {
    years -= 1;
  }
  return years;
}

/**
 * Validate employee date of birth.
 * @returns {string|null} Error message or null when valid.
 */
export function validateDateOfBirth(value, referenceDate = new Date()) {
  const raw = String(value || "").trim();
  if (!raw) return null;

  if (!ISO_DATE_RE.test(raw)) {
    return "Enter a valid date with a 4-digit year (YYYY-MM-DD)";
  }

  const year = Number(raw.slice(0, 4));
  if (year < 1000 || year > 9999) {
    return "Year must be exactly 4 digits";
  }

  if (!isValidIsoDateString(raw)) {
    return "Enter a valid date with a 4-digit year (YYYY-MM-DD)";
  }

  const ref = referenceDate instanceof Date ? referenceDate : parseDateValue(referenceDate);
  const maxStr = toDateValue(ref || new Date());
  if (raw > maxStr) {
    return "Date of birth cannot be in the future";
  }

  const minStr = toDateValue(getDobMinDate(ref || new Date()));
  if (raw < minStr) {
    return "Please give correct date of birth year. Employee age must be below 100 years.";
  }

  const age = getAgeYears(raw, ref || new Date());
  if (age !== null && age >= 100) {
    return "Please give correct date of birth year. Employee age must be below 100 years.";
  }

  return null;
}

/** Validate YYYY-MM month keys (4-digit year). */
export function validateMonthKey(value) {
  const raw = String(value || "").trim();
  if (!/^\d{4}-\d{2}$/.test(raw)) {
    return "Year must be exactly 4 digits (YYYY-MM)";
  }
  const year = Number(raw.slice(0, 4));
  const month = Number(raw.slice(5, 7));
  if (year < 1000 || year > 9999 || month < 1 || month > 12) {
    return "Enter a valid month with a 4-digit year";
  }
  return null;
}

export function parseDateTimeValue(value) {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function formatDisplayDate(value) {
  const date = parseDateValue(value);
  if (!date) return "";
  return date.toLocaleDateString(undefined, {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

export function formatDisplayDateTime(value) {
  const date = parseDateTimeValue(value);
  if (!date) return "";
  return date.toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

export function buildCalendarCells(viewYear, viewMonth) {
  const first = new Date(viewYear, viewMonth, 1);
  const startOffset = (first.getDay() + 6) % 7;
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const daysInPrev = new Date(viewYear, viewMonth, 0).getDate();
  const cells = [];

  for (let i = startOffset - 1; i >= 0; i -= 1) {
    const day = daysInPrev - i;
    cells.push({
      date: new Date(viewYear, viewMonth - 1, day),
      inMonth: false,
    });
  }

  for (let day = 1; day <= daysInMonth; day += 1) {
    cells.push({
      date: new Date(viewYear, viewMonth, day),
      inMonth: true,
    });
  }

  while (cells.length % 7 !== 0) {
    const day = cells.length - (startOffset + daysInMonth) + 1;
    cells.push({
      date: new Date(viewYear, viewMonth + 1, day),
      inMonth: false,
    });
  }

  return cells;
}

export function isSameDay(a, b) {
  if (!a || !b) return false;
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

export function monthLabel(year, month) {
  return `${MONTHS[month]} ${year}`;
}

/** Combine work date + HH:mm into datetime-local style string. */
export function combineDateAndTime(workDate, timeStr) {
  if (!workDate || !timeStr) return "";
  const [h, m] = String(timeStr).split(":");
  return `${workDate}T${pad2(Number(h) || 0)}:${pad2(Number(m) || 0)}`;
}

/** Hours between check-in/out on the same work day (checkout next day if earlier). */
export function calcWorkHoursFromParts(workDate, checkInTime, checkOutTime) {
  if (!workDate || !checkInTime || !checkOutTime) return null;
  const inD = new Date(combineDateAndTime(workDate, checkInTime));
  let outD = new Date(combineDateAndTime(workDate, checkOutTime));
  if (Number.isNaN(inD.getTime()) || Number.isNaN(outD.getTime())) return null;
  if (outD <= inD) outD = new Date(outD.getTime() + 86400000);
  return Math.round(((outD - inD) / 3600000) * 100) / 100;
}

export { MONTHS, WEEKDAYS };
